import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

import Block from './Block';
import CommandMenu from './CommandMenu';
import ProjectsBlock from './blocks/ProjectsBlock';
import CreateProjectBlock from './blocks/CreateProjectBlock';
import QuickActionsBlock from './blocks/QuickActionsBlock';
import RecentActivityBlock from './blocks/RecentActivityBlock';
import ApiKeysBlock from './blocks/ApiKeysBlock';
import AnalyticsBlock from './blocks/AnalyticsBlock';
import PromptLogsBlock from './blocks/PromptLogsBlock';
import SharedPromptsBlock from './blocks/SharedPromptsBlock';
import ExperimentsBlock from './blocks/ExperimentsBlock';
import TestSetsBlock from './blocks/TestSetsBlock';
import ComposerFlowsBlock from './blocks/ComposerFlowsBlock';
import ProjectCreationModal from '../ProjectCreationModal';
import { getDashboardLayout, saveDashboardLayout } from '../../services/api';

// Block type components mapping
const BLOCK_COMPONENTS = {
  'projects': ProjectsBlock,
  'create-project': CreateProjectBlock,
  'test-sets': TestSetsBlock,
  'composer-flows': ComposerFlowsBlock,
  'quick-actions': QuickActionsBlock,
  'recent-activity': RecentActivityBlock,
  'api-keys': ApiKeysBlock,
  'analytics': AnalyticsBlock,
  'prompt-logs': PromptLogsBlock,
  'shared-prompts': SharedPromptsBlock,
  'experiments': ExperimentsBlock,
};

const DEFAULT_BLOCKS = [
  {
    id: 'projects-1',
    type: 'projects',
    size: 'medium',
    title: 'Projects'
  },
  {
    id: 'create-project-1',
    type: 'create-project',
    size: 'medium',
    title: 'Create Project'
  },
  {
    id: 'quick-actions-1',
    type: 'quick-actions',
    size: 'medium',
    title: 'Quick Actions'
  },
  {
    id: 'recent-activity-1',
    type: 'recent-activity',
    size: 'medium',
    title: 'Recent Activity'
  }
];

// Create refresh context for real-time updates
const RefreshContext = createContext({
  refreshAll: () => {},
  refreshBlock: () => {},
  refreshTriggers: {},
  openProjectCreationModal: () => {}
});

export const useRefresh = () => useContext(RefreshContext);

// Icons
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const SparklesIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const BlockCanvas = ({ user }) => {
  const [blocks, setBlocks] = useState(DEFAULT_BLOCKS);
  const [activeId, setActiveId] = useState(null);
  const [commandMenuOpen, setCommandMenuOpen] = useState(false);
  const [commandMenuPosition, setCommandMenuPosition] = useState(null);
  const [refreshTriggers, setRefreshTriggers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showProjectCreationModal, setShowProjectCreationModal] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Refresh functions for real-time updates
  const refreshAll = useCallback(() => {
    const timestamp = Date.now();
    setRefreshTriggers(prev => ({
      ...prev,
      all: timestamp,
      projects: timestamp,
      'test-sets': timestamp,
      'composer-flows': timestamp,
      experiments: timestamp,
      'recent-activity': timestamp,
      'prompt-logs': timestamp,
      analytics: timestamp,
      'api-keys': timestamp,
      'shared-prompts': timestamp
    }));
  }, []);

  const refreshBlock = useCallback((blockType) => {
    setRefreshTriggers(prev => ({
      ...prev,
      [blockType]: Date.now()
    }));
  }, []);

  const openProjectCreationModal = useCallback(() => {
    setShowProjectCreationModal(true);
  }, []);

  const refreshContext = {
    refreshAll,
    refreshBlock,
    refreshTriggers,
    openProjectCreationModal
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '/') {
        e.preventDefault();
        setCommandMenuPosition({ x: 0, y: 0 });
        setCommandMenuOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Load user's block configuration from backend
  useEffect(() => {
    const loadUserLayout = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await getDashboardLayout(user.id);
        if (response.layout) {
          setBlocks(response.layout);
        }
      } catch (error) {
        console.error('Failed to load user layout:', error);
        // Fallback to localStorage for backward compatibility
        const savedBlocks = localStorage.getItem(`blocks-${user.id}`);
        if (savedBlocks) {
          try {
            setBlocks(JSON.parse(savedBlocks));
          } catch (parseError) {
            console.error('Failed to parse saved blocks:', parseError);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadUserLayout();
  }, [user]);

  // Save blocks to backend and localStorage
  const saveBlocks = useCallback(async (newBlocks) => {
    if (!user?.id) return;
    
    try {
      await saveDashboardLayout(user.id, newBlocks);
      // Also save to localStorage as backup
      localStorage.setItem(`blocks-${user.id}`, JSON.stringify(newBlocks));
    } catch (error) {
      console.error('Failed to save layout to backend:', error);
      // Fallback to localStorage only
      localStorage.setItem(`blocks-${user.id}`, JSON.stringify(newBlocks));
    }
  }, [user]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setBlocks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newBlocks = arrayMove(items, oldIndex, newIndex);
        saveBlocks(newBlocks);
        return newBlocks;
      });
    }

    setActiveId(null);
  };

  const handleAddBlock = (blockType) => {
    const newBlock = {
      id: `${blockType.id}-${Date.now()}`,
      type: blockType.id,
      size: 'medium',
      title: blockType.name
    };

    const newBlocks = [...blocks, newBlock];
    setBlocks(newBlocks);
    saveBlocks(newBlocks);
    setCommandMenuOpen(false);
  };

  const handleRemoveBlock = (blockId) => {
    const newBlocks = blocks.filter(block => block.id !== blockId);
    setBlocks(newBlocks);
    saveBlocks(newBlocks);
  };

  const handleResizeBlock = (blockId, newSize) => {
    const newBlocks = blocks.map(block =>
      block.id === blockId ? { ...block, size: newSize } : block
    );
    setBlocks(newBlocks);
    saveBlocks(newBlocks);
  };

  const handleProjectCreated = (newProject) => {
    // Refresh projects block and create-project block
    refreshBlock('projects');
    refreshBlock('create-project');
  };

  const renderBlock = (block) => {
    const BlockComponent = BLOCK_COMPONENTS[block.type];
    
    if (!BlockComponent) {
      return (
        <div className="flex items-center justify-center h-full text-center p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center mx-auto mb-4">
              <SparklesIcon />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Unknown block type</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{block.type}</p>
          </div>
        </div>
      );
    }

    const blockProps = {
      user,
      size: block.size,
      onProjectCreated: handleProjectCreated,
      refreshTrigger: refreshTriggers[block.type] || refreshTriggers.all,
      onRefresh: () => refreshBlock(block.type),
      onOpenProjectCreationModal: openProjectCreationModal,
    };

    return <BlockComponent {...blockProps} />;
  };

  const activeBlock = blocks.find(block => block.id === activeId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 font-medium">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <RefreshContext.Provider value={refreshContext}>
      <div className="space-y-8">
        {/* Block Canvas */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={blocks.map(block => block.id)} 
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-6 gap-6 max-w-none">
              {blocks.map((block) => (
                <Block
                  key={block.id}
                  id={block.id}
                  size={block.size}
                  title={block.title}
                  onRemove={handleRemoveBlock}
                  onResize={handleResizeBlock}
                  isDragging={activeId === block.id}
                >
                  {renderBlock(block)}
                </Block>
              ))}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeBlock ? (
              <Block
                id={activeBlock.id}
                size={activeBlock.size}
                title={activeBlock.title}
                className="opacity-90 shadow-strong"
              >
                {renderBlock(activeBlock)}
              </Block>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Empty state if no blocks */}
        {blocks.length === 0 && (
          <div className="text-center py-20">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-gradient-to-br from-celadon-400 to-celadon-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_8px_40px_-8px_rgba(180,210,186,0.3)]">
                <SparklesIcon />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Your workspace is ready
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                Start building your personalized dashboard by adding blocks. 
                Press <kbd className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-xl text-sm font-mono mx-1 shadow-soft">
                  /
                </kbd> 
                to get started.
              </p>
              <button
                onClick={() => {
                  setCommandMenuPosition({ x: 0, y: 0 });
                  setCommandMenuOpen(true);
                }}
                className="btn-primary btn-lg"
              >
                <PlusIcon />
                <span className="ml-2">Add Your First Block</span>
              </button>
            </div>
          </div>
        )}

        {/* Command Menu */}
        <CommandMenu
          isOpen={commandMenuOpen}
          onClose={() => setCommandMenuOpen(false)}
          onSelectBlock={handleAddBlock}
          position={commandMenuPosition}
        />

        {/* Project Creation Modal */}
        <ProjectCreationModal
          isOpen={showProjectCreationModal}
          onClose={() => setShowProjectCreationModal(false)}
          user={user}
          onProjectCreated={handleProjectCreated}
        />
      </div>
    </RefreshContext.Provider>
  );
};

export default BlockCanvas; 